import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Plus, ChevronDown, LayoutList, LayoutGrid, Filter, Columns, Zap, Loader2, CheckCircle, Edit, UserPlus, Flag, Trash2, Eye, ArrowRight } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { ExceptionCardView } from '@/components/exceptions/ExceptionCardView';
import { ExceptionListView } from '@/components/exceptions/ExceptionListView';
import { ExceptionDetailPanel } from '@/components/exceptions/ExceptionDetailPanel';
import { ExceptionPagination } from '@/components/exceptions/ExceptionPagination';
import { ExceptionLoadingState } from '@/components/exceptions/ExceptionLoadingState';
import { ContactAuditorModal } from '@/components/exceptions/ContactAuditorModal';
import type { ContactAuditorData } from '@/components/exceptions/ContactAuditorModal';
import { ResolveExceptionModal } from '@/components/exceptions/ResolveExceptionModal';
import type { ResolveExceptionData } from '@/components/exceptions/ResolveExceptionModal';
import { AssignUserModal } from '@/components/exceptions/AssignUserModal';
import type { AssignUserData } from '@/components/exceptions/AssignUserModal';
import { CreateManualExceptionModal } from '@/components/exceptions/CreateManualExceptionModal';
import type { ManualExceptionData } from '@/components/exceptions/CreateManualExceptionModal';
import { EmptyState } from '@/components/exceptions/EmptyState';
import { ExceptionType, ExceptionStatus, ExceptionSeverity, ExceptionPriority } from '@/types/asset-audit';
import type { AuditException } from '@/types/asset-audit';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Import the new components
import { ExceptionMetricsCards } from '@/components/audits/exceptions/ExceptionMetricsCards';
import { ExceptionFiltersTab } from '@/components/exceptions/ExceptionFiltersTab';
import { ExceptionColumnsTab } from '@/components/exceptions/ExceptionColumnsTab';

// Import tour components
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useExceptionsTour } from '@/hooks/use-exceptions-tour';

type ViewMode = 'card' | 'list';

const STATUS_OPTIONS: ExceptionStatus[] = [
  ExceptionStatus.Open,
  ExceptionStatus.InProgress,
  ExceptionStatus.ActionRequired,
  ExceptionStatus.Resolved,
];

const PRIORITY_OPTIONS: ExceptionPriority[] = [
  ExceptionPriority.Urgent,
  ExceptionPriority.High,
  ExceptionPriority.Medium,
  ExceptionPriority.Low,
];

// Mock data for demonstration
const mockExceptions: AuditException[] = [
  {
    id: '1',
    auditId: 'AUD-001',
    auditNumber: 'AUD-001',
    assetId: 'AST-001',
    assetCode: 'AST-001',
    assetName: 'Server Room A - Rack 1',
    exceptionType: ExceptionType.DataDiscrepancy,
    description: 'Asset tag is missing from the physical equipment. Unable to verify asset identity during physical audit.',
    location: 'Building A, Server Room',
    severity: ExceptionSeverity.Critical,
    status: ExceptionStatus.Open,
    priority: ExceptionPriority.High,
    reportedBy: 'user-1',
    reportedByName: 'John Doe',
    reportedDate: '2024-01-15T10:30:00Z',
    requiresFollowUp: true,
    followUpDueDate: '2024-01-20T10:30:00Z',
    assignedTo: 'user-1',
    assignedToName: 'John Doe',
    expectedValue: 'AST-001-TAG',
    actualValue: 'Missing',
    comments: [],
    images: [
      {
        id: 'img-1',
        url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400',
        thumbnailUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=200',
        fileName: 'server-rack.jpg',
        fileSize: 245000,
        mimeType: 'image/jpeg',
        uploadedDate: '2024-01-15T10:30:00Z',
        uploadedBy: 'user-1',
        uploadedByName: 'John Doe',
      },
    ],
  },
  {
    id: '2',
    auditId: 'AUD-001',
    auditNumber: 'AUD-001',
    assetId: 'AST-002',
    assetCode: 'AST-002',
    assetName: 'Laptop - Dell XPS 15',
    exceptionType: ExceptionType.LocationMismatch,
    description: 'Asset recorded in Building A but physically located in Building B.',
    location: 'Building B, Floor 1, Room 105',
    severity: ExceptionSeverity.High,
    status: ExceptionStatus.InProgress,
    priority: ExceptionPriority.High,
    reportedBy: 'user-2',
    reportedByName: 'Jane Smith',
    reportedDate: '2024-01-14T09:15:00Z',
    requiresFollowUp: true,
    followUpDueDate: '2024-01-25T09:15:00Z',
    assignedTo: 'user-2',
    assignedToName: 'Jane Smith',
    expectedLocation: 'Building A, Floor 2, Room 201',
    actualLocation: 'Building B, Floor 1, Room 105',
    comments: [],
    images: [
      {
        id: 'img-2',
        url: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400',
        thumbnailUrl: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=200',
        fileName: 'laptop.jpg',
        fileSize: 180000,
        mimeType: 'image/jpeg',
        uploadedDate: '2024-01-14T09:15:00Z',
        uploadedBy: 'user-2',
        uploadedByName: 'Jane Smith',
      },
    ],
  },
  {
    id: '3',
    auditId: 'AUD-001',
    auditNumber: 'AUD-001',
    assetId: 'AST-003',
    assetCode: 'AST-003',
    assetName: 'Network Switch - Cisco 2960',
    exceptionType: ExceptionType.ConditionIssue,
    description: 'Asset condition marked as "Good" but shows visible damage and wear.',
    location: 'Building C, Network Closet',
    severity: ExceptionSeverity.Medium,
    status: ExceptionStatus.Open,
    priority: ExceptionPriority.Medium,
    reportedBy: 'user-1',
    reportedByName: 'John Doe',
    reportedDate: '2024-01-13T16:45:00Z',
    requiresFollowUp: true,
    followUpDueDate: '2024-01-28T16:45:00Z',
    assignedTo: 'user-1',
    assignedToName: 'John Doe',
    expectedValue: 'Good',
    actualValue: 'Fair - Visible wear',
    comments: [],
  },
  {
    id: '4',
    auditId: 'AUD-001',
    auditNumber: 'AUD-001',
    assetId: 'AST-004',
    assetCode: 'AST-004',
    assetName: 'Monitor - Samsung 27"',
    exceptionType: ExceptionType.DataDiscrepancy,
    description: 'Serial number on physical asset does not match database record.',
    severity: ExceptionSeverity.High,
    status: ExceptionStatus.Resolved,
    priority: ExceptionPriority.High,
    reportedBy: 'user-2',
    reportedByName: 'Jane Smith',
    reportedDate: '2024-01-12T11:20:00Z',
    resolvedDate: '2024-01-16T14:30:00Z',
    resolvedBy: 'user-2',
    resolvedByName: 'Jane Smith',
    requiresFollowUp: false,
    assignedTo: 'user-2',
    assignedToName: 'Jane Smith',
    expectedValue: 'SN-12345',
    actualValue: 'SN-67890',
    comments: [],
  },
  {
    id: '5',
    auditId: 'AUD-001',
    auditNumber: 'AUD-001',
    assetId: 'AST-005',
    assetCode: 'AST-005',
    assetName: 'Printer - HP LaserJet Pro',
    exceptionType: ExceptionType.DuplicateAsset,
    description: 'Asset found during audit but not present in asset database.',
    severity: ExceptionSeverity.Critical,
    status: ExceptionStatus.Open,
    priority: ExceptionPriority.Urgent,
    reportedBy: 'user-1',
    reportedByName: 'John Doe',
    reportedDate: '2024-01-16T08:00:00Z',
    requiresFollowUp: true,
    followUpDueDate: '2024-01-18T08:00:00Z',
    expectedValue: 'Not in database',
    actualValue: 'Physical asset found',
    comments: [],
  },
  {
    id: '6',
    auditId: '',
    auditNumber: '',
    assetId: 'AST-006',
    assetCode: 'AST-006',
    assetName: 'Desktop - HP EliteDesk',
    exceptionType: ExceptionType.ComplianceIssue,
    description: 'Asset does not meet current compliance standards for security.',
    severity: ExceptionSeverity.High,
    status: ExceptionStatus.ActionRequired,
    priority: ExceptionPriority.High,
    reportedBy: 'user-1',
    reportedByName: 'John Doe',
    reportedDate: '2024-01-11T14:00:00Z',
    requiresFollowUp: true,
    followUpDueDate: '2024-01-22T14:00:00Z',
    assignedTo: 'user-1',
    assignedToName: 'John Doe',
    comments: [],
  },
];

export const ExceptionsRedesigned: React.FC = () => {
  const { toast } = useToast();
  
  // Tour hooks
  const tour = useExceptionsTour();
  const { setOnStartTour } = useTour();

  // State
  const [exceptions] = useState<AuditException[]>(mockExceptions);
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading] = useState(false);
  const [selectedException, setSelectedException] = useState<AuditException | null>(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [createManualModalOpen, setCreateManualModalOpen] = useState(false);
  const [modalException, setModalException] = useState<AuditException | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isPriorityDialogOpen, setIsPriorityDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ExceptionStatus>(ExceptionStatus.Open);
  const [selectedPriority, setSelectedPriority] = useState<ExceptionPriority>(ExceptionPriority.Medium);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'severity',
    'asset',
    'exceptionType',
    'description',
    'status',
    'reportedBy',
    'reportedDate',
  ]);

  // Filters state
  const [tableFilters, setTableFilters] = useState<{
    status: ExceptionStatus[];
    severity: ExceptionSeverity[];
    priority: ExceptionPriority[];
    type: ExceptionType[];
    assignedTo: string[];
    reportedBy: string[];
  }>({
    status: [],
    severity: [],
    priority: [],
    type: [],
    assignedTo: [],
    reportedBy: [],
  });

  // Register tour with context
  React.useEffect(() => {
    setOnStartTour(() => tour.startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, tour]);

  // Reset filters handler
  const handleResetFilters = () => {
    setSearchTerm('');
    setActiveQuickFilter(null);
    setTableFilters({
      status: [],
      severity: [],
      priority: [],
      type: [],
      assignedTo: [],
      reportedBy: [],
    });
  };

  // Computed values
  const criticalCount = useMemo(() => 
    exceptions.filter(e => e.severity === ExceptionSeverity.Critical).length,
    [exceptions]
  );



  // Filter and sort exceptions
  const filteredAndSortedExceptions = useMemo(() => {
    let filtered = [...exceptions];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(exception =>
        exception.exceptionType.toLowerCase().includes(searchLower) ||
        exception.description?.toLowerCase().includes(searchLower) ||
        exception.assetCode.toLowerCase().includes(searchLower) ||
        exception.assetName?.toLowerCase().includes(searchLower) ||
        exception.reportedByName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply quick filters
    if (activeQuickFilter) {
      switch (activeQuickFilter) {
        case 'critical':
          filtered = filtered.filter(e => e.severity === ExceptionSeverity.Critical);
          break;
        case 'overdue':
          filtered = filtered.filter(e => e.followUpDueDate && new Date(e.followUpDueDate) < new Date());
          break;
        case 'my-exceptions':
          // Filter by the current user (user-1 in this mock)
          filtered = filtered.filter(e => e.reportedBy === 'user-1');
          break;
        case 'unassigned':
          filtered = filtered.filter(e => !e.assignedTo);
          break;
        case 'follow-up':
          filtered = filtered.filter(e => e.requiresFollowUp);
          break;
      }
    }

    // Apply table filters
    if (tableFilters.status.length > 0) {
      filtered = filtered.filter(e => tableFilters.status.includes(e.status));
    }
    if (tableFilters.severity.length > 0) {
      filtered = filtered.filter(e => tableFilters.severity.includes(e.severity));
    }
    if (tableFilters.priority.length > 0) {
      filtered = filtered.filter(e => e.priority && tableFilters.priority.includes(e.priority));
    }
    if (tableFilters.type.length > 0) {
      filtered = filtered.filter(e => tableFilters.type.includes(e.exceptionType));
    }
    if (tableFilters.assignedTo.length > 0) {
      filtered = filtered.filter(e => e.assignedTo && tableFilters.assignedTo.includes(e.assignedTo));
    }
    if (tableFilters.reportedBy.length > 0) {
      filtered = filtered.filter(e => tableFilters.reportedBy.includes(e.reportedBy));
    }

    return filtered;
  }, [exceptions, searchTerm, activeQuickFilter, tableFilters]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedExceptions.length / pageSize);
  const paginatedExceptions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedExceptions.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedExceptions, currentPage, pageSize]);

  // Handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleToggleSelection = (exceptionId: string) => {
    const newSelection = new Set(selectedRowIds);
    if (newSelection.has(exceptionId)) {
      newSelection.delete(exceptionId);
    } else {
      newSelection.add(exceptionId);
    }
    setSelectedRowIds(newSelection);
  };

  const handleSelectException = (exception: AuditException) => {
    setSelectedException(exception);
    setIsDetailPanelOpen(true);
  };

  const handleCloseDetailPanel = () => {
    setIsDetailPanelOpen(false);
    setSelectedException(null);
  };

  const handleUpdateStatus = (exceptionId: string, status: string) => {
    toast({
      title: 'Status Updated',
      description: `Exception status changed to ${status}`,
    });
  };

  const handleAddComment = (exceptionId: string, comment: string) => {
    toast({
      title: 'Comment Added',
      description: 'Your comment has been added to the exception',
    });
  };

  const handleContactAuditor = (exception: AuditException) => {
    setModalException(exception);
    setContactModalOpen(true);
  };

  const handleSendMessage = (data: ContactAuditorData) => {
    toast({
      title: 'Message Sent',
      description: `Message sent to ${data.to}`,
    });
    setContactModalOpen(false);
  };

  const handleResolveException = (exception: AuditException) => {
    setModalException(exception);
    setResolveModalOpen(true);
  };

  const handleResolve = (data: ResolveExceptionData) => {
    toast({
      title: 'Exception Resolved',
      description: 'The exception has been marked as resolved',
    });
    setResolveModalOpen(false);
  };

  const handleAssignUser = (exception: AuditException) => {
    setModalException(exception);
    setAssignModalOpen(true);
  };

  const handleCreateWorkItem = (exception: AuditException) => {
    toast({
      title: 'Create Work Item',
      description: `Opening work item creation for exception ${exception.id}`,
    });
  };

  const handleViewAsset = (assetId: string | number) => {
    toast({
      title: 'View Asset',
      description: `Navigating to asset ${assetId}`,
    });
  };

  const handleViewAudit = (auditId: string | number) => {
    toast({
      title: 'View Audit',
      description: `Navigating to audit ${auditId}`,
    });
  };

  const handleEditException = (exception: AuditException) => {
    setSelectedException(exception);
    setIsDetailPanelOpen(true);
  };

  const handleChangePriority = (exception: AuditException) => {
    toast({
      title: 'Change Priority',
      description: `Opening priority change for exception ${exception.id}`,
    });
  };

  const handleDismissException = (exception: AuditException) => {
    toast({
      title: 'Dismiss Exception',
      description: `Exception ${exception.id} has been dismissed`,
    });
  };

  // Handlers for InteractiveMetricsCards
  const handleFilterByType = (type: string) => {
    const typeEnum = type.replace(/ /g, '') as ExceptionType;
    setTableFilters(prev => ({
      ...prev,
      type: prev.type.includes(typeEnum) ? prev.type.filter(t => t !== typeEnum) : [...prev.type, typeEnum],
    }));
    setCurrentPage(1);
  };

  const handleFilterBySeverity = (severity: string) => {
    const severityEnum = severity.charAt(0).toUpperCase() + severity.slice(1) as ExceptionSeverity;
    setTableFilters(prev => ({
      ...prev,
      severity: prev.severity.includes(severityEnum) ? prev.severity.filter(s => s !== severityEnum) : [...prev.severity, severityEnum],
    }));
    setCurrentPage(1);
  };

  const handleFilterByStatus = (status: string) => {
    const statusEnum = status.replace(/ /g, '') as ExceptionStatus;
    setTableFilters(prev => ({
      ...prev,
      status: prev.status.includes(statusEnum) ? prev.status.filter(s => s !== statusEnum) : [...prev.status, statusEnum],
    }));
    setCurrentPage(1);
  };

  const handleFilterFollowUp = (filter: string) => {
    // Set the follow-up filter using the quick filter mechanism
    setActiveQuickFilter(activeQuickFilter === 'follow-up' ? null : 'follow-up');
    setCurrentPage(1);
  };

  const handleAssign = (data: AssignUserData) => {
    toast({
      title: 'User Assigned',
      description: `Exception assigned to ${data.userName}`,
    });
    setAssignModalOpen(false);
  };

  const handleCreateException = () => {
    setCreateManualModalOpen(true);
  };

  const handleCreateManualException = (data: ManualExceptionData) => {
    toast({
      title: 'Exception Created',
      description: 'Manual exception has been created successfully',
    });
    setCreateManualModalOpen(false);
  };

  const handleMarkAsResolved = async () => {
    setBulkActionLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: 'Exceptions Resolved',
      description: `${selectedRowIds.size} exception(s) marked as resolved`,
    });
    setSelectedRowIds(new Set());
    setBulkActionLoading(false);
  };

  const handleBulkStatusChange = async () => {
    setBulkActionLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: 'Status Updated',
      description: `${selectedRowIds.size} exception(s) status changed to ${selectedStatus}`,
    });
    setSelectedRowIds(new Set());
    setIsStatusDialogOpen(false);
    setBulkActionLoading(false);
  };

  const handleBulkPriorityChange = async () => {
    setBulkActionLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: 'Priority Updated',
      description: `${selectedRowIds.size} exception(s) priority changed to ${selectedPriority}`,
    });
    setSelectedRowIds(new Set());
    setIsPriorityDialogOpen(false);
    setBulkActionLoading(false);
  };

  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: 'Exceptions Deleted',
      description: `${selectedRowIds.size} exception(s) deleted`,
      variant: 'destructive',
    });
    setSelectedRowIds(new Set());
    setIsDeleteDialogOpen(false);
    setBulkActionLoading(false);
  };

  // Action Card Component
  const ActionCard = ({
    icon: Icon,
    title,
    description,
    onClick,
    variant,
    disabled
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // Custom Actions Tab Component
  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-xs text-muted-foreground">
              Select one or more exceptions to see available actions
            </p>
          </div>
        ) : (
          <>
            {/* Bulk Actions */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Bulk Actions
              </h4>
              <ActionCard
                icon={CheckCircle}
                title="Mark as Resolved"
                description={`Mark ${selectedRowIds.size} exception${selectedRowIds.size !== 1 ? 's' : ''} as resolved`}
                variant="action"
                onClick={handleMarkAsResolved}
                disabled={bulkActionLoading}
              />
              <ActionCard
                icon={Edit}
                title="Change Status"
                description="Update status for selected exceptions"
                variant="action"
                onClick={() => setIsStatusDialogOpen(true)}
                disabled={bulkActionLoading}
              />
              <ActionCard
                icon={UserPlus}
                title="Assign to User"
                description="Assign selected exceptions to a user"
                variant="action"
                onClick={() => setAssignModalOpen(true)}
                disabled={bulkActionLoading}
              />
              <ActionCard
                icon={Flag}
                title="Change Priority"
                description="Update priority level for exceptions"
                variant="action"
                onClick={() => setIsPriorityDialogOpen(true)}
                disabled={bulkActionLoading}
              />
            </div>

            {/* Other Actions */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Other Actions
              </h4>
              <ActionCard
                icon={Trash2}
                title="Delete Exceptions"
                description={`Permanently remove ${selectedRowIds.size} exception${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="delete"
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={bulkActionLoading}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Define page-specific tabs for the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: (
        <ExceptionFiltersTab 
          exceptions={exceptions} 
          activeQuickFilter={activeQuickFilter} 
          onQuickFilterChange={setActiveQuickFilter}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={tableFilters}
          onFiltersChange={setTableFilters}
        />
      ),
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: (
        <ExceptionColumnsTab
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      ),
      badgeCount: visibleColumns.length,
    },
  ];

  const getViewIcon = () => {
    switch (viewMode) {
      case 'list':
        return <LayoutList className="h-4 w-4" />;
      case 'card':
        return <LayoutGrid className="h-4 w-4" />;
    }
  };

  const getViewLabel = () => {
    switch (viewMode) {
      case 'list':
        return 'List View';
      case 'card':
        return 'Card View';
    }
  };

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6" data-tour="exceptions-header">
          <div className="flex items-center justify-between">
            <div>
              {/* Title with Icon */}
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold tracking-tight">Audit Exceptions</h1>
                {criticalCount > 0 && (
                  <Badge variant="destructive" className="animate-pulse">
                    {criticalCount} Critical
                  </Badge>
                )}
              </div>
              
              {/* Subtitle */}
              <p className="text-sm text-muted-foreground mt-1">
                Review and manage audit discrepancies and exceptions
              </p>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="default" data-tour="exceptions-view-mode">
                    {getViewIcon()}
                    <span className="ml-2">{getViewLabel()}</span>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setViewMode('list')}>
                    <LayoutList className="mr-2 h-4 w-4" />
                    List View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setViewMode('card')}>
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    Card View
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button onClick={handleCreateException} size="default" data-tour="exceptions-new-button">
                <Plus className="mr-2 h-4 w-4" />
                New Exception
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-auto">
          {/* Summary Cards - Inside scrollable area with padding */}
          <div className="px-6 py-4" data-tour="exceptions-metrics">
            <ExceptionMetricsCards
              exceptions={filteredAndSortedExceptions}
              onFilterByType={handleFilterByType}
              onFilterBySeverity={handleFilterBySeverity}
              onFilterByStatus={handleFilterByStatus}
              onFilterFollowUp={handleFilterFollowUp}
              onResetFilters={handleResetFilters}
            />
          </div>

          {isLoading && (
            <div className="px-6">
              <ExceptionLoadingState viewMode={viewMode} count={pageSize} />
            </div>
          )}
          {!isLoading && filteredAndSortedExceptions.length === 0 && (
            <div className="px-6">
              <EmptyState
                hasFilters={!!searchTerm || !!activeQuickFilter || Object.values(tableFilters).some(f => 
                  Array.isArray(f) ? f.length > 0 : f !== undefined && f !== ''
                )}
                onClearFilters={() => {
                  setSearchTerm('');
                  setActiveQuickFilter(null);
                  setTableFilters({
                    status: [],
                    severity: [],
                    priority: [],
                    type: [],
                    assignedTo: [],
                    reportedBy: [],
                  });
                }}
              />
            </div>
          )}
          {!isLoading && filteredAndSortedExceptions.length > 0 && viewMode === 'card' && (
            <div className="px-6" data-tour="exceptions-list">
              <ExceptionCardView
                exceptions={paginatedExceptions}
                onSelectException={handleSelectException}
                selectedExceptions={selectedRowIds}
                onToggleSelection={handleToggleSelection}
                onContactAuditor={handleContactAuditor}
                onResolve={handleResolveException}
                onCreateWorkItem={handleCreateWorkItem}
                onViewAsset={handleViewAsset}
                onViewAudit={handleViewAudit}
                onEdit={handleEditException}
                onChangePriority={handleChangePriority}
                onDismiss={handleDismissException}
              />
            </div>
          )}
          {!isLoading && filteredAndSortedExceptions.length > 0 && viewMode === 'list' && (
            <div data-tour="exceptions-list">
              <ExceptionListView
                exceptions={paginatedExceptions}
              selectedRowIds={selectedRowIds}
              onSelectionChange={handleToggleSelection}
              onSelectAll={(checked) => {
                if (checked) {
                  setSelectedRowIds(new Set(paginatedExceptions.map(e => e.id)));
                } else {
                  setSelectedRowIds(new Set());
                }
              }}
              visibleColumns={visibleColumns}
              />
            </div>
          )}

          {/* Pagination */}
          {filteredAndSortedExceptions.length > 0 && (
            <div className="px-6 py-4">
              <ExceptionPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filteredAndSortedExceptions.length}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} />

      {/* Detail Panel */}
      <ExceptionDetailPanel
        exception={selectedException}
        isOpen={isDetailPanelOpen}
        onClose={handleCloseDetailPanel}
        onUpdateStatus={handleUpdateStatus}
        onAddComment={handleAddComment}
      />

      {/* Modals */}
      <ContactAuditorModal
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
        exception={modalException}
        onSend={handleSendMessage}
      />

      <ResolveExceptionModal
        open={resolveModalOpen}
        onOpenChange={setResolveModalOpen}
        exception={modalException}
        onResolve={handleResolve}
      />

      <AssignUserModal
        open={assignModalOpen}
        onOpenChange={setAssignModalOpen}
        exception={modalException}
        onAssign={handleAssign}
      />

      <CreateManualExceptionModal
        open={createManualModalOpen}
        onOpenChange={setCreateManualModalOpen}
        onSubmit={handleCreateManualException}
      />

      {/* Change Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Select a new status for {selectedRowIds.size} selected exception{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedStatus} onValueChange={(value: ExceptionStatus) => setSelectedStatus(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkStatusChange} disabled={bulkActionLoading}>
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Priority Dialog */}
      <Dialog open={isPriorityDialogOpen} onOpenChange={setIsPriorityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Priority</DialogTitle>
            <DialogDescription>
              Select a new priority for {selectedRowIds.size} selected exception{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPriority} onValueChange={(value: ExceptionPriority) => setSelectedPriority(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((priority) => (
                  <SelectItem key={priority} value={priority}>
                    {priority}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPriorityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkPriorityChange} disabled={bulkActionLoading}>
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Priority
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedRowIds.size} exception{selectedRowIds.size !== 1 ? 's' : ''}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {bulkActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Product Tour */}
      <ProductTour
        steps={tour.tourSteps}
        isOpen={tour.isTourOpen}
        onClose={tour.closeTour}
        onComplete={tour.completeTour}
      />
    </PageWithRightPanel>
  );
};
